import { asDateString } from './member-update-payload.util';

const LINE_ITEM_FIELDS = [
  'name',
  'productId',
  'description',
  'quantity',
  'totalQuantity',
  'unit',
  'cost',
  'wholeSalePrice',
  'normalPrice',
  'discount',
  'subTotal',
  'SaleOrderProductLastQuantity',
  'restockDate',
  'code',
  'vatTax',
  'lastSoldPrice',
] as const;

function buildPurchaseOrderLineItem(raw: Record<string, unknown>): Record<string, unknown> {
  const item: Record<string, unknown> = {};
  const productId = raw.productId ?? raw._id;
  if (productId) item.productId = String(productId);

  for (const key of LINE_ITEM_FIELDS) {
    if (key === 'productId') continue;
    const value = key === 'cost'
      ? raw.cost ?? raw.unitPrice
      : raw[key];
    if (value === undefined || value === null) continue;
    if (key === 'restockDate') {
      const date = asDateString(value);
      if (date) item.restockDate = date;
    } else if (typeof value === 'number') {
      item[key] = value;
    } else {
      item[key] = String(value);
    }
  }

  return item;
}

export interface PurchaseOrderCreateOptions {
  company: string;
  branch: string;
  createdBy?: string;
  supplier?: unknown;
}

/** Body for POST .../branches/:branchId/purchase-orders (CreatePurchaseOrderDto). */
export function buildPurchaseOrderCreatePayload(
  order: object,
  products: object[],
  options: PurchaseOrderCreateOptions,
): Record<string, unknown> {
  const o = (order || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    company: options.company,
    branch: options.branch,
    products: (products || []).map((p) => buildPurchaseOrderLineItem(p as Record<string, unknown>)),
  };

  if (options.supplier) payload.supplier = options.supplier;
  if (options.createdBy) payload.createdBy = options.createdBy;
  if (o.remarks !== undefined && o.remarks !== null) payload.remarks = String(o.remarks);
  if (o.subTotal !== undefined && o.subTotal !== null) payload.subTotal = Number(o.subTotal);

  return payload;
}

export interface PurchaseOrderUpdateOptions {
  company: string;
  branch: string;
  updatedBy?: string;
  supplier?: unknown;
}

/** Body for PATCH .../branches/:branchId/purchase-orders/:id (UpdatePurchaseOrderDto). */
export function buildPurchaseOrderUpdatePayload(
  order: object,
  products: object[],
  options: PurchaseOrderUpdateOptions,
): Record<string, unknown> {
  const o = (order || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    company: options.company,
    branch: options.branch,
    products: (products || []).map((p) => buildPurchaseOrderLineItem(p as Record<string, unknown>)),
  };

  if (options.supplier) payload.supplier = options.supplier;
  if (options.updatedBy) payload.updatedBy = options.updatedBy;
  if (o.remarks !== undefined && o.remarks !== null) payload.remarks = String(o.remarks);
  if (o.subTotal !== undefined && o.subTotal !== null) payload.subTotal = Number(o.subTotal);
  if (o.total !== undefined && o.total !== null) payload.total = Number(o.total);
  if (o.remainingSubtotal !== undefined && o.remainingSubtotal !== null) {
    payload.remainingSubtotal = Number(o.remainingSubtotal);
  }
  if (o.paid !== undefined && o.paid !== null) payload.paid = Number(o.paid);
  if (o.returned !== undefined && o.returned !== null) payload.returned = Number(o.returned);
  if (o.vatTax !== undefined && o.vatTax !== null) payload.vatTax = Number(o.vatTax);
  if (o.isActive !== undefined) payload.isActive = Boolean(o.isActive);
  if (o.isDelete !== undefined) payload.isDelete = Boolean(o.isDelete);

  return payload;
}

export interface PurchaseOrderFulfillOptions {
  company: string;
  branch: string;
  paid: number;
  paymentMode?: string;
  updatedBy?: string;
  supplier?: unknown;
}

/** Body for PATCH .../branches/:branchId/purchase-orders/:id/actions/fulfill (FulfillPurchaseOrderDto). */
export function buildPurchaseOrderFulfillPayload(
  order: object,
  products: object[],
  options: PurchaseOrderFulfillOptions,
): Record<string, unknown> {
  const o = (order || {}) as Record<string, unknown>;
  const payload: Record<string, unknown> = {
    company: options.company,
    branch: options.branch,
    paid: options.paid,
    products: (products || []).map((p) => buildPurchaseOrderLineItem(p as Record<string, unknown>)),
  };

  if (options.supplier) payload.supplier = options.supplier;
  if (options.paymentMode) payload.paymentMode = options.paymentMode;
  if (options.updatedBy) payload.updatedBy = options.updatedBy;
  if (o.remarks !== undefined && o.remarks !== null) payload.remarks = String(o.remarks);
  if (o.subTotal !== undefined && o.subTotal !== null) payload.subTotal = Number(o.subTotal);
  if (o.total !== undefined && o.total !== null) payload.total = Number(o.total);
  if (o.vatTax !== undefined && o.vatTax !== null) payload.vatTax = Number(o.vatTax);

  return payload;
}

function buildProductStockItem(raw: Record<string, unknown>): Record<string, unknown> | null {
  const productId = raw.productId ?? raw._id;
  if (!productId) return null;

  const quantity = Number(raw.quantity);
  const cost = Number(raw.cost ?? raw.unitPrice);
  if (Number.isNaN(quantity) || Number.isNaN(cost)) return null;

  return {
    productId: String(productId),
    quantity,
    unit: raw.unit != null ? String(raw.unit) : '',
    cost,
    normalPrice: Number(raw.normalPrice ?? 0),
    wholeSalePrice: Number(raw.wholeSalePrice ?? 0),
  };
}

/** Body for PATCH .../branches/:branchId/purchase-orders/actions/update-products-stock. */
export function buildPurchaseOrderProductsStockPayload(
  products: object[],
): { products: Record<string, unknown>[] } {
  return {
    products: (products || [])
      .map((p) => buildProductStockItem(p as Record<string, unknown>))
      .filter((p): p is Record<string, unknown> => p != null),
  };
}
